import { useEffect, useState } from "react";
import { Box, Button, Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, Alert, Chip, Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField } from "@mui/material";
import { Add, PictureAsPdf, Code, Refresh, Chat, Receipt, Email, Replay, DeleteOutline } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import clientBillingApi from "../../../api/clientBillingApi";
import { InvoiceStatusChip } from "../components/InvoiceStatusChip";
import { ClientInvoiceStatus } from "hvp-shared";

interface PendingTicket {
  invoiceNumber: string;
  ticketDisplay: string;
  date: string;
  total: number;
  branch: string;
}

interface LinkedQvetCustomer {
  qvetCustomerId: number;
  customerName: string;
  lastVisitDate: string | null;
}

interface InvoiceItem {
  id: string;
  invoiceNumber: number;
  ticketDisplay?: string;
  date: string;
  total: number;
  status: ClientInvoiceStatus;
  cfdiUuid?: string;
  folioCfdi?: string;
  dateRule?: string;
  warningMessage?: string;
  branch?: string;
  createdAt: string;
}

export const InvoiceListPage = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [pendingTickets, setPendingTickets] = useState<PendingTicket[]>([]);
  const [linkedCustomers, setLinkedCustomers] = useState<LinkedQvetCustomer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [requestingTicket, setRequestingTicket] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({ open: false, message: "", severity: "success" });
  const [emailDialogId, setEmailDialogId] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState("");
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchInvoices = async () => {
    try {
      setIsLoading(true);
      const [invRes, pendingRes, linkedRes] = await Promise.all([
        clientBillingApi.get("/client-billing/invoices"),
        clientBillingApi.get("/client-billing/invoices/pending-tickets").catch(() => ({ data: { data: [] } })),
        clientBillingApi.get("/client-billing/qvet-customers").catch(() => ({ data: { data: [] } })),
      ]);
      setInvoices(invRes.data.data || []);
      setPendingTickets(pendingRes.data.data || []);
      setLinkedCustomers(linkedRes.data.data || []);
    } catch {
      console.error("Error fetching invoices");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestPending = async (ticket: PendingTicket) => {
    try {
      setRequestingTicket(ticket.invoiceNumber);
      await clientBillingApi.post("/client-billing/invoices", {
        invoiceNumber: ticket.invoiceNumber,
        amount: ticket.total,
      });
      fetchInvoices();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error?.message || "Error al solicitar factura", severity: "error" });
    } finally {
      setRequestingTicket(null);
    }
  };

  const handleRetry = async (id: string) => {
    try {
      setRetryingId(id);
      const { data } = await clientBillingApi.post(`/client-billing/invoices/${id}/retry`);
      const newStatus = data?.data?.status;
      if (newStatus === ClientInvoiceStatus.stamped) {
        setSnackbar({ open: true, message: "Factura timbrada exitosamente", severity: "success" });
      } else if (newStatus === ClientInvoiceStatus.failed) {
        setSnackbar({
          open: true,
          message: data?.data?.errorMessage || "El reintento volvió a fallar",
          severity: "error",
        });
      } else {
        setSnackbar({ open: true, message: "Reintento en proceso", severity: "success" });
      }
      fetchInvoices();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error?.message || "Error al reintentar",
        severity: "error",
      });
    } finally {
      setRetryingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleting(true);
      await clientBillingApi.delete(`/client-billing/invoices/${deleteId}`);
      setSnackbar({ open: true, message: "Solicitud eliminada", severity: "success" });
      setDeleteId(null);
      fetchInvoices();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error?.message || "Error al eliminar",
        severity: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleDownload = async (id: string, type: "pdf" | "xml") => {
    try {
      const response = await clientBillingApi.get(`/client-billing/invoices/${id}/${type}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `factura-${id}.${type}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setSnackbar({ open: true, message: "Error al descargar", severity: "error" });
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <Container maxWidth="lg">
      {pendingTickets.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, backgroundColor: "#fff8e1" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Receipt color="warning" />
            <Typography variant="h6">
              Tickets pendientes de facturar ({pendingTickets.length})
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Fecha ticket</TableCell>
                  <TableCell>Sucursal</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="center">Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingTickets.map((t) => (
                  <TableRow key={t.invoiceNumber}>
                    <TableCell sx={{ fontWeight: "bold" }}>{t.ticketDisplay}</TableCell>
                    <TableCell>{formatDate(t.date)}</TableCell>
                    <TableCell><Chip label={t.branch} size="small" /></TableCell>
                    <TableCell align="right">${t.total.toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        disabled={requestingTicket === t.invoiceNumber}
                        onClick={() => handleRequestPending(t)}
                      >
                        {requestingTicket === t.invoiceNumber ? "Solicitando..." : "Facturar"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {linkedCustomers.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
              Clientes QVET vinculados:
            </Typography>
            {linkedCustomers.map((c) => (
              <Chip
                key={c.qvetCustomerId}
                size="small"
                label={`${c.customerName || "(sin nombre)"} · #${c.qvetCustomerId}`}
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Mis Facturas
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchInvoices}
            disabled={isLoading}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/facturacion/nueva")}
          >
            Solicitar factura
          </Button>
        </Box>
      </Box>

      {invoices.length === 0 && !isLoading ? (
        <Paper sx={{ p: 6, textAlign: "center" }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Aún no tienes facturas
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Solicita tu primera factura con el número de ticket de tu visita.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate("/facturacion/nueva")}>
            Solicitar factura
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ticket</TableCell>
                <TableCell>Folio CFDI</TableCell>
                <TableCell>Fecha ticket</TableCell>
                <TableCell>Sucursal</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>UUID</TableCell>
                <TableCell align="center">Descargar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow key={inv.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/facturacion/facturas/${inv.id}`)}>
                  <TableCell>{inv.ticketDisplay || inv.invoiceNumber}</TableCell>
                  <TableCell>{inv.folioCfdi || "-"}</TableCell>
                  <TableCell>{formatDate(inv.date)}</TableCell>
                  <TableCell>{inv.branch || "-"}</TableCell>
                  <TableCell align="right">${inv.total?.toFixed(2)}</TableCell>
                  <TableCell><InvoiceStatusChip status={inv.status} /></TableCell>
                  <TableCell sx={{ fontSize: 11, fontFamily: "monospace" }}>{inv.cfdiUuid || "-"}</TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    {inv.status === ClientInvoiceStatus.failed && (
                      <>
                        <Tooltip title="Reintentar">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={retryingId === inv.id}
                              onClick={() => handleRetry(inv.id)}
                            >
                              <Replay />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Eliminar solicitud">
                          <IconButton size="small" color="error" onClick={() => setDeleteId(inv.id)}>
                            <DeleteOutline />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {inv.status === ClientInvoiceStatus.requires_authorization && (
                      <Tooltip title="Solicitar autorización por WhatsApp">
                        <IconButton
                          size="small"
                          color="success"
                          component="a"
                          href={`https://wa.me/5219991841434?text=${encodeURIComponent(`Hola, solicito autorización para facturar el ticket ${inv.ticketDisplay || inv.invoiceNumber} del mes anterior`)}`}
                          target="_blank"
                        >
                          <Chat />
                        </IconButton>
                      </Tooltip>
                    )}
                    {inv.status === ClientInvoiceStatus.stamped && (
                      <>
                        <Tooltip title="Descargar PDF">
                          <IconButton size="small" onClick={() => handleDownload(inv.id, "pdf")}>
                            <PictureAsPdf color="error" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Descargar XML">
                          <IconButton size="small" onClick={() => handleDownload(inv.id, "xml")}>
                            <Code color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Enviar por email">
                          <IconButton size="small" onClick={() => { setEmailDialogId(inv.id); setEmailInput(""); }}>
                            <Email color="action" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={!!emailDialogId} onClose={() => setEmailDialogId(null)}>
        <DialogTitle>Enviar factura por correo</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>Ingresa el correo al que deseas enviar el PDF y XML.</DialogContentText>
          <TextField autoFocus fullWidth label="Email" type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && emailInput && emailDialogId) {
                const id = emailDialogId; setEmailDialogId(null);
                clientBillingApi.post(`/client-billing/invoices/${id}/send-email`, { email: emailInput })
                  .then(() => setSnackbar({ open: true, message: "Email enviado", severity: "success" }))
                  .catch((err: any) => setSnackbar({ open: true, message: err.response?.data?.error?.message || "Error", severity: "error" }));
              }
            }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogId(null)}>Cancelar</Button>
          <Button variant="contained" disabled={!emailInput} onClick={() => {
            const id = emailDialogId; setEmailDialogId(null);
            clientBillingApi.post(`/client-billing/invoices/${id}/send-email`, { email: emailInput })
              .then(() => setSnackbar({ open: true, message: "Email enviado", severity: "success" }))
              .catch((err: any) => setSnackbar({ open: true, message: err.response?.data?.error?.message || "Error", severity: "error" }));
          }}>Enviar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => !deleting && setDeleteId(null)}>
        <DialogTitle>Eliminar solicitud</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar esta solicitud con error? Podrás volver a solicitarla después.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)} disabled={deleting}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};
